/**
 * Stock module state: intakes, the open document, suppliers, ingredients.
 *
 * Everything here is a copy of what the cloud returned. The dashboard does
 * not compute on-hand, portions or totals — it displays what came back and
 * appends events through the cloud. See docs/feature-stock-intake-and-
 * suppliers.md §10.
 */
import { Map } from "immutable";
import { orderBy } from "lodash";
import { ModuleEvents } from "./Actions";

const initialState = Map({
  intakes: [],
  intakesLastKey: null,
  selectedIntake: null,
  suppliers: [],
  ingredients: [],
  /** Last error from a stock call, so a screen can show it inline. */
  stockError: null,
});

/** Replace a document in the list by id, or prepend it when it is new. */
const upsertIntake = (list, intake) => {
  if (!intake) return list;
  const idx = list.findIndex((i) => i.intakeId === intake.intakeId);
  if (idx === -1) return [intake, ...list];
  const next = [...list];
  next[idx] = intake;
  return next;
};

const upsertBy = (list, key, item) => {
  if (!item) return list;
  const idx = list.findIndex((i) => i[key] === item[key]);
  if (idx === -1) return [...list, item];
  const next = [...list];
  next[idx] = item;
  return next;
};

export const Reducer = (state = initialState, action) => {
  const { payload, type } = action;
  const failed = !payload || payload.error;

  switch (type) {
    case ModuleEvents.FETCH_INTAKES: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      return state
        .set("intakes", payload.intakes ?? [])
        .set("intakesLastKey", payload.lastKey ?? null)
        .set("stockError", null);
    }

    case ModuleEvents.FETCH_INTAKE:
    case ModuleEvents.CREATE_INTAKE:
    case ModuleEvents.UPDATE_INTAKE: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      return state
        .set("selectedIntake", payload.intake)
        .set("intakes", upsertIntake(state.get("intakes"), payload.intake))
        .set("stockError", null);
    }

    case ModuleEvents.POST_INTAKE:
    case ModuleEvents.VOID_INTAKE: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      // The response also carries `targets` — what each line is now worth.
      // The editor reads that from the action, not from here: it is a result
      // of one operation, not state the screen should keep.
      return state
        .set("selectedIntake", payload.intake)
        .set("intakes", upsertIntake(state.get("intakes"), payload.intake))
        .set("stockError", null);
    }

    case ModuleEvents.DELETE_INTAKE: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      return state
        .set("intakes", state.get("intakes").filter((i) => i.intakeId !== payload.intakeId))
        .set("selectedIntake", null)
        .set("stockError", null);
    }

    case ModuleEvents.CLEAR_SELECTED_INTAKE:
      return state.set("selectedIntake", null).set("stockError", null);

    case ModuleEvents.FETCH_SUPPLIERS: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      return state
        .set("suppliers", orderBy(payload.suppliers ?? [], ["name"], ["asc"]))
        .set("stockError", null);
    }

    case ModuleEvents.CREATE_SUPPLIER:
    case ModuleEvents.UPDATE_SUPPLIER:
    case ModuleEvents.DEACTIVATE_SUPPLIER: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      const next = upsertBy(state.get("suppliers"), "supplierId", payload.supplier);
      return state
        .set("suppliers", orderBy(next, ["name"], ["asc"]))
        .set("stockError", null);
    }

    case ModuleEvents.FETCH_INGREDIENTS: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      return state
        .set("ingredients", orderBy(payload.ingredients ?? [], ["itemName"], ["asc"]))
        .set("stockError", null);
    }

    case ModuleEvents.CREATE_INGREDIENT:
    case ModuleEvents.UPDATE_INGREDIENT:
    case ModuleEvents.DEACTIVATE_INGREDIENT: {
      if (failed) return state.set("stockError", payload?.error ?? null);
      const next = upsertBy(state.get("ingredients"), "stockId", payload.ingredient);
      return state
        .set("ingredients", orderBy(next, ["itemName"], ["asc"]))
        .set("stockError", null);
    }

    default:
      return state;
  }
};

export default Reducer;
